/**
 * WaOutboxPanel — Widget per inviare le notifiche WhatsApp pendenti.
 *
 * Mostra la lista delle WhatsApp pending (notifications_outbox.channel="whatsapp", sent!=true)
 * con un pulsante "Invia" per ciascuna che apre wa.me con messaggio precompilato.
 *
 * Se la notifica non ha phone (utente non ha salvato il proprio numero nel profilo),
 * il pulsante "Invia" mostra un input inline per inserire il numero al volo.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { MessageSquare, RefreshCw, CheckCircle2, Phone, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTimeIT } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";

function buildWAUrl(phone, text) {
  const cleaned = (phone || "").replace(/[^\d+]/g, "");
  const t = encodeURIComponent(text || "");
  return `https://wa.me/${cleaned}?text=${t}`;
}

export default function WaOutboxPanel() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [phoneInputs, setPhoneInputs] = useState({}); // {outboxId: "+39..."}
  const [savingProfile, setSavingProfile] = useState(false);

  const userHasPhone = !!(user?.phone);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/wa-outbox", { params: { pending: true } });
      setItems(r.data?.items || []);
    } catch {
      // silent
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const markSent = async (it) => {
    try {
      await api.patch(`/wa-outbox/${it.id}/sent`, { sent: true });
      setItems((arr) => arr.filter((x) => x.id !== it.id));
      toast.success("Marcato come inviato");
    } catch { toast.error("Errore"); }
  };

  const sendNow = (it, overridePhone = null) => {
    const phone = overridePhone || it.to?.phone || phoneInputs[it.id];
    if (!phone || !phone.replace(/[^\d+]/g, "")) {
      toast.error("Inserisci un numero prima di inviare");
      return;
    }
    const url = buildWAUrl(phone, it.message);
    window.open(url, "_blank", "noopener,noreferrer");
    markSent(it);
  };

  // Salva il phone come default sul profilo + invia subito
  const saveProfilePhoneAndSend = async (it) => {
    const phone = (phoneInputs[it.id] || "").trim();
    if (!phone || !phone.replace(/[^\d+]/g, "")) {
      toast.error("Inserisci un numero valido");
      return;
    }
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", { phone });
      toast.success("Numero salvato nel profilo");
      // Invia subito
      sendNow(it, phone);
      // Refresh user in context (forziamo reload nascosto)
      window.dispatchEvent(new Event("ba-user-updated"));
    } catch {
      toast.error("Errore salvataggio profilo");
    } finally {
      setSavingProfile(false);
    }
  };

  if (items.length === 0 && !loading) return null;

  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 mb-4" data-testid="wa-outbox-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-emerald-900">
          <MessageSquare className="w-4 h-4" />
          Notifiche WhatsApp da inviare ({items.length})
        </div>
        <div className="flex items-center gap-1">
          <button onClick={load} className="text-emerald-700 hover:bg-emerald-100 rounded-sm p-1" title="Aggiorna">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={() => setCollapsed(!collapsed)} className="text-emerald-700 hover:bg-emerald-100 rounded-sm px-2 text-xs">
            {collapsed ? "Espandi" : "Nascondi"}
          </button>
        </div>
      </div>

      {!userHasPhone && (
        <div className="mb-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm p-2 text-[11px] text-amber-800">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            Non hai ancora salvato il tuo numero di telefono. Per ricevere automaticamente le notifiche WhatsApp, inseriscilo nel <a href="/profilo" className="font-semibold underline">Profilo</a>, oppure inserisci un numero qui sotto al volo.
          </span>
        </div>
      )}

      {!collapsed && (
        <div className="space-y-1.5">
          {items.map((it) => {
            const hasItemPhone = !!(it.to?.phone || "").replace(/[^\d+]/g, "");
            return (
              <div key={it.id} className="bg-white border border-emerald-100 rounded-sm p-2.5 flex items-start gap-3 text-xs"
                data-testid={`wa-outbox-${it.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-slate-900 font-medium">
                    <Phone className="w-3 h-3 text-slate-400" />
                    {it.to?.name || "Agente"} · <span className={hasItemPhone ? "text-slate-900" : "text-rose-600 italic"}>{hasItemPhone ? it.to.phone : "numero mancante"}</span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">Creato {fmtDateTimeIT(it.created_at)}</div>
                  <pre className="mt-1.5 text-[11px] text-slate-700 whitespace-pre-wrap font-sans bg-slate-50 border border-slate-100 rounded-sm p-2">{it.message}</pre>
                  {!hasItemPhone && (
                    <div className="mt-2 flex items-center gap-1.5">
                      <input
                        type="tel"
                        placeholder="+39 333 1234567"
                        value={phoneInputs[it.id] || ""}
                        onChange={(e) => setPhoneInputs((p) => ({ ...p, [it.id]: e.target.value }))}
                        className="flex-1 px-2 py-1.5 text-xs border border-slate-300 rounded-sm"
                        data-testid={`wa-outbox-phone-input-${it.id}`}
                      />
                      <button
                        onClick={() => saveProfilePhoneAndSend(it)}
                        disabled={savingProfile || !(phoneInputs[it.id] || "").trim()}
                        data-testid={`wa-outbox-save-send-${it.id}`}
                        className="px-2.5 py-1.5 bg-[#0A192F] hover:bg-[#0D1E35] text-white rounded-sm text-[11px] disabled:opacity-50"
                        title="Salva nel tuo profilo e invia subito"
                      >
                        Salva + Invia
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    onClick={() => sendNow(it)}
                    disabled={!hasItemPhone && !(phoneInputs[it.id] || "").trim()}
                    data-testid={`wa-outbox-send-${it.id}`}
                    className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs flex items-center gap-1 disabled:opacity-50"
                  >
                    <MessageSquare className="w-3 h-3" /> Invia
                  </button>
                  <button
                    onClick={() => markSent(it)}
                    data-testid={`wa-outbox-skip-${it.id}`}
                    className="px-2.5 py-1.5 border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-sm text-xs flex items-center gap-1"
                    title="Marca come inviato senza aprire WhatsApp"
                  >
                    <CheckCircle2 className="w-3 h-3" /> Salta
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
